/*
 * atm-facets.js — theme-native faceted filtering engine for Atmósfera.
 *
 * Replaces the (uninstalled) Search & Discovery app. Reads `data-facet-*`
 * attributes emitted per-card by snippets/atm-facets.liquid (mode: 'attrs'),
 * builds the facet groups + counts entirely client-side, filters cards
 * in-place (show/hide), keeps state in the URL querystring, and renders
 * active-filter chips.
 *
 * Works on any root marked `[data-atm-facets-root]` that contains:
 *   - product cards: `[data-component-card]` (also carries `data-component-text`
 *     for the existing "buscar componente" free-text search)
 *   - a facets shell (from atm-facets.liquid mode:'shell'):
 *       [data-atm-facets-body]   <- facet <details> groups get injected here
 *       [data-component-search]  <- free-text input (existing markup, reused)
 *       [data-atm-clear-all]     <- "Limpiar" button
 *   - a results bar:
 *       [data-atm-count]  <- "N productos" text
 *       [data-atm-chips]  <- active filter chips container
 *
 * No dependency on collection.filters / Search & Discovery. Multiple roots
 * per page are supported (each is scoped independently). Re-run safely after
 * an AJAX section swap by dispatching `atm:results-updated` on `document`
 * with `{ detail: { root } }`.
 */
(function () {
  "use strict";

  var FACET_DEFS = [
    { key: "vendor", label: "Marca", attr: "facetVendor", param: "f_vendor", openByDefault: true },
    { key: "cpu", label: "Procesador", attr: "facetCpu", param: "f_cpu" },
    { key: "gpu", label: "Gráfica", attr: "facetGpu", param: "f_gpu" },
    { key: "ram", label: "RAM", attr: "facetRam", param: "f_ram" },
    { key: "storage", label: "Almacenamiento", attr: "facetStorage", param: "f_storage" },
    { key: "screen", label: "Pantalla", attr: "facetScreen", param: "f_screen" },
    { key: "condition", label: "Condición", attr: "facetCondition", param: "f_condition", openByDefault: true }
  ];

  var ORDERS = {
    cpu: [
      "Intel Core Ultra 9", "Intel Core Ultra 7", "Intel Core Ultra 5",
      "Intel Core i9", "Intel Core i7", "Intel Core i5", "Intel Core i3",
      "Intel Core 7", "Intel Core 5",
      "AMD Ryzen AI 9", "AMD Ryzen AI 7", "AMD Ryzen AI 5",
      "AMD Ryzen 9", "AMD Ryzen 7", "AMD Ryzen 5", "AMD Ryzen 3",
      "Apple M5", "Qualcomm Snapdragon X"
    ],
    gpu: [
      "RTX 5090", "RTX 5080", "RTX 5070 Ti", "RTX 5070", "RTX 5060", "RTX 5050",
      "RTX 4090", "RTX 4080", "RTX 4070 Ti", "RTX 4070", "RTX 4060", "RTX 4050",
      "RTX 3070", "RTX 3060", "RTX 3050", "RTX 2050",
      "AMD Radeon", "Integrada"
    ],
    condition: ["Nuevo", "Open Box", "Exhibición"]
  };

  var DIACRITICS_RE = new RegExp("[̀-ͯ]", "g");
  var norm = function (value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(DIACRITICS_RE, "")
      .trim();
  };

  var escapeHtml = function (value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (ch) {
      return ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch];
    });
  };

  var parseNum = function (value) {
    var n = parseFloat(String(value || "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  var storageSortValue = function (value) {
    var n = parseNum(value);
    return /TB/i.test(value) ? n * 1024 : n;
  };

  var sortFacetValues = function (defKey, values) {
    var order = ORDERS[defKey];
    if (order) {
      return values.slice().sort(function (a, b) {
        var ia = order.indexOf(a);
        var ib = order.indexOf(b);
        if (ia === -1 && ib === -1) return a.localeCompare(b, "es");
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    if (defKey === "ram" || defKey === "screen") {
      return values.slice().sort(function (a, b) { return parseNum(a) - parseNum(b); });
    }
    if (defKey === "storage") {
      return values.slice().sort(function (a, b) { return storageSortValue(a) - storageSortValue(b); });
    }
    return values.slice().sort(function (a, b) { return a.localeCompare(b, "es"); });
  };

  var pluralProductos = function (n) {
    return n === 1 ? "producto" : "productos";
  };

  function createEmptyFacetSets() {
    var facets = {};
    FACET_DEFS.forEach(function (def) { facets[def.key] = new Set(); });
    return facets;
  }

  function parseStateFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var facets = createEmptyFacetSets();
    FACET_DEFS.forEach(function (def) {
      var raw = params.get(def.param);
      if (!raw) return;
      raw.split(",").map(function (v) { return v.trim(); }).filter(Boolean).forEach(function (v) {
        facets[def.key].add(v);
      });
    });
    var priceMin = params.has("f_price_min") ? parseFloat(params.get("f_price_min")) : null;
    var priceMax = params.has("f_price_max") ? parseFloat(params.get("f_price_max")) : null;
    return {
      facets: facets,
      priceMin: Number.isFinite(priceMin) ? priceMin : null,
      priceMax: Number.isFinite(priceMax) ? priceMax : null,
      text: params.get("fq") || ""
    };
  }

  function syncUrlFromState(state) {
    try {
      var url = new URL(window.location.href);
      var params = url.searchParams;
      FACET_DEFS.forEach(function (def) {
        var vals = Array.from(state.facets[def.key]);
        if (vals.length) params.set(def.param, vals.join(",")); else params.delete(def.param);
      });
      if (state.priceMin != null) params.set("f_price_min", String(state.priceMin)); else params.delete("f_price_min");
      if (state.priceMax != null) params.set("f_price_max", String(state.priceMax)); else params.delete("f_price_max");
      if (state.text) params.set("fq", state.text); else params.delete("fq");
      window.history.replaceState(window.history.state, "", url);
    } catch (err) { /* URL sync is best-effort */ }
  }

  function hasActiveFilters(state) {
    var any = FACET_DEFS.some(function (def) { return state.facets[def.key].size > 0; });
    return any || state.priceMin != null || state.priceMax != null || !!state.text;
  }

  function matchesFilters(card, state, exceptKey) {
    for (var i = 0; i < FACET_DEFS.length; i++) {
      var def = FACET_DEFS[i];
      if (def.key === exceptKey) continue;
      var selected = state.facets[def.key];
      if (selected.size === 0) continue;
      if (!selected.has(card.dataset[def.attr] || "")) return false;
    }
    if (exceptKey !== "price") {
      var price = parseFloat(card.dataset.facetPrice || "0");
      if (state.priceMin != null && price < state.priceMin) return false;
      if (state.priceMax != null && price > state.priceMax) return false;
    }
    if (state.text) {
      var haystack = norm(card.dataset.componentText || "");
      if (!haystack.includes(norm(state.text))) return false;
    }
    return true;
  }

  function countsForFacet(cards, state, def) {
    var counts = new Map();
    cards.forEach(function (card) {
      if (!matchesFilters(card, state, def.key)) return;
      var raw = card.dataset[def.attr];
      if (!raw) return;
      counts.set(raw, (counts.get(raw) || 0) + 1);
    });
    return counts;
  }

  function facetGroupHtml(def, counts, state) {
    var values = sortFacetValues(def.key, Array.from(counts.keys()));
    if (!values.length) return "";
    var selected = state.facets[def.key];
    var open = def.openByDefault || selected.size > 0;
    var rows = values.map(function (value) {
      var count = counts.get(value) || 0;
      var checked = selected.has(value);
      var disabled = count === 0 && !checked;
      return (
        '<label class="atm-cat__opt' + (disabled ? " is-disabled" : "") + '">' +
        '<input type="checkbox" data-atm-facet-input data-facet-key="' + def.key + '" value="' + escapeHtml(value) + '"' +
        (checked ? " checked" : "") + (disabled ? " disabled" : "") + ">" +
        '<span class="atm-cat__box" aria-hidden="true"><svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.2l2.3 2.3 4.7-5"/></svg></span>' +
        '<span class="atm-cat__optlabel">' + escapeHtml(value) + "</span>" +
        '<span class="atm-cat__optcount">' + count + "</span>" +
        "</label>"
      );
    }).join("");
    return (
      '<details class="atm-cat__group"' + (open ? " open" : "") + ">" +
      '<summary class="atm-cat__grouptitle">' + escapeHtml(def.label) +
      '<svg class="atm-cat__chev" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true"><path d="M3 5l4 4 4-4"/></svg>' +
      "</summary>" +
      '<div class="atm-cat__groupbody">' + rows + "</div>" +
      "</details>"
    );
  }

  function priceGroupHtml(cards, state) {
    var prices = cards.map(function (c) { return parseFloat(c.dataset.facetPrice || "0"); }).filter(function (n) { return n > 0; });
    if (!prices.length) return "";
    var maxPrice = Math.max.apply(null, prices);
    var minPlaceholder = "0";
    var maxPlaceholder = String(Math.ceil(maxPrice));
    return (
      '<details class="atm-cat__group" open>' +
      '<summary class="atm-cat__grouptitle">Precio' +
      '<svg class="atm-cat__chev" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true"><path d="M3 5l4 4 4-4"/></svg>' +
      "</summary>" +
      '<div class="atm-cat__groupbody">' +
      '<div class="atm-cat__price-f">' +
      '<label class="atm-cat__pricefield"><span>Mín.</span><input type="number" inputmode="decimal" min="0" placeholder="' + minPlaceholder + '" data-atm-price="min" value="' + (state.priceMin != null ? state.priceMin : "") + '"></label>' +
      '<label class="atm-cat__pricefield"><span>Máx.</span><input type="number" inputmode="decimal" min="0" placeholder="' + maxPlaceholder + '" data-atm-price="max" value="' + (state.priceMax != null ? state.priceMax : "") + '"></label>' +
      "</div></div></details>"
    );
  }

  function buildChips(state) {
    var chips = [];
    FACET_DEFS.forEach(function (def) {
      state.facets[def.key].forEach(function (value) {
        chips.push({
          html: (
            '<button type="button" class="atm-cat__chip" data-atm-chip-remove data-facet-key="' + def.key + '" data-facet-value="' + escapeHtml(value) + '">' +
            "<span>" + escapeHtml(def.label) + ": " + escapeHtml(value) + "</span>" +
            '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M3 3l6 6M9 3 3 9"/></svg>' +
            "</button>"
          )
        });
      });
    });
    if (state.priceMin != null || state.priceMax != null) {
      var label = "Precio: ";
      if (state.priceMin != null && state.priceMax != null) label += "$" + state.priceMin.toLocaleString("es-CO") + " – $" + state.priceMax.toLocaleString("es-CO");
      else if (state.priceMin != null) label += "desde $" + state.priceMin.toLocaleString("es-CO");
      else label += "hasta $" + state.priceMax.toLocaleString("es-CO");
      chips.push({
        html: (
          '<button type="button" class="atm-cat__chip" data-atm-chip-remove data-facet-key="price" data-facet-value="">' +
          "<span>" + escapeHtml(label) + "</span>" +
          '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M3 3l6 6M9 3 3 9"/></svg>' +
          "</button>"
        )
      });
    }
    if (state.text) {
      chips.push({
        html: (
          '<button type="button" class="atm-cat__chip" data-atm-chip-remove data-facet-key="text" data-facet-value="">' +
          "<span>Buscar: " + escapeHtml(state.text) + "</span>" +
          '<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M3 3l6 6M9 3 3 9"/></svg>' +
          "</button>"
        )
      });
    }
    return chips;
  }

  // All listeners are delegated on the STABLE root (the section element),
  // never on nodes inside [data-atm-facets-body]/[data-atm-chips] — those get
  // their innerHTML replaced on every refresh(), which would silently detach
  // any listener bound directly to them (and after an AJAX sort/page swap the
  // whole results subtree, aside included, is replaced too). Binding once on
  // `root` survives all of that.
  function bindRootEvents(root) {
    var textTimer = null;
    var priceTimer = null;

    root.addEventListener("change", function (event) {
      var input = event.target.closest("[data-atm-facet-input]");
      if (!input) return;
      var state = root.__atmState;
      var key = input.dataset.facetKey;
      var value = input.value;
      if (input.checked) state.facets[key].add(value); else state.facets[key].delete(value);
      commit(root);
    });

    root.addEventListener("input", function (event) {
      var priceInput = event.target.closest("[data-atm-price]");
      if (priceInput) {
        window.clearTimeout(priceTimer);
        priceTimer = window.setTimeout(function () {
          var state = root.__atmState;
          var which = priceInput.dataset.atmPrice;
          var num = priceInput.value === "" ? null : parseFloat(priceInput.value);
          if (which === "min") state.priceMin = Number.isFinite(num) ? num : null;
          else state.priceMax = Number.isFinite(num) ? num : null;
          commit(root);
        }, 400);
        return;
      }
      if (event.target.matches("[data-component-search]")) {
        var searchInput = event.target;
        window.clearTimeout(textTimer);
        textTimer = window.setTimeout(function () {
          root.__atmState.text = searchInput.value.trim();
          commit(root);
        }, 250);
      }
    });

    root.addEventListener("click", function (event) {
      if (event.target.closest("[data-atm-clear-all]")) {
        var state = root.__atmState;
        state.facets = createEmptyFacetSets();
        state.priceMin = null;
        state.priceMax = null;
        state.text = "";
        var searchInput = root.querySelector("[data-component-search]");
        if (searchInput) searchInput.value = "";
        commit(root);
        return;
      }
      var chipBtn = event.target.closest("[data-atm-chip-remove]");
      if (chipBtn) {
        var st = root.__atmState;
        var key = chipBtn.dataset.facetKey;
        if (key === "price") {
          st.priceMin = null;
          st.priceMax = null;
        } else if (key === "text") {
          st.text = "";
          var input2 = root.querySelector("[data-component-search]");
          if (input2) input2.value = "";
        } else {
          st.facets[key].delete(chipBtn.dataset.facetValue);
        }
        commit(root);
      }
    });
  }

  function initRoot(root) {
    if (!root) return;
    if (!root.__atmState) root.__atmState = parseStateFromUrl();

    var searchInput = root.querySelector("[data-component-search]");
    if (searchInput) searchInput.value = root.__atmState.text || "";

    if (root.dataset.atmFacetsBound !== "true") {
      root.dataset.atmFacetsBound = "true";
      bindRootEvents(root);
    }

    refresh(root);
  }

  function commit(root) {
    syncUrlFromState(root.__atmState);
    refresh(root);
  }

  function refresh(root) {
    var state = root.__atmState;
    if (!state) return;
    var cards = Array.from(root.querySelectorAll("[data-component-card]"));
    var body = root.querySelector("[data-atm-facets-body]");

    if (body) {
      // Preserve focus + cursor position on a price input across the rebuild
      // (the group HTML — including the price inputs — is fully regenerated
      // every refresh so counts stay correct, which would otherwise steal
      // focus mid-typing).
      var activeEl = document.activeElement;
      var activePriceKind = activeEl && body.contains(activeEl) && activeEl.matches("[data-atm-price]")
        ? activeEl.dataset.atmPrice : null;

      var html = "";
      FACET_DEFS.forEach(function (def) {
        html += facetGroupHtml(def, countsForFacet(cards, state, def), state);
      });
      html += priceGroupHtml(cards, state);
      if (html) body.innerHTML = html;

      if (activePriceKind) {
        var restored = body.querySelector('[data-atm-price="' + activePriceKind + '"]');
        if (restored) {
          restored.focus();
          var pos = restored.value.length;
          try { restored.setSelectionRange(pos, pos); } catch (err) { /* number inputs may not support selection */ }
        }
      }
    }

    var visible = 0;
    cards.forEach(function (card) {
      var show = matchesFilters(card, state, null);
      card.classList.toggle("is-component-hidden", !show);
      if (show) visible += 1;
    });

    var total = cards.length;
    var countEl = root.querySelector("[data-atm-count]");
    if (countEl) {
      countEl.textContent = hasActiveFilters(state)
        ? visible + " de " + total + " " + pluralProductos(total)
        : total + " " + pluralProductos(total);
    }

    var chipsWrap = root.querySelector("[data-atm-chips]");
    if (chipsWrap) {
      var chips = buildChips(state);
      if (chips.length) {
        chipsWrap.hidden = false;
        chipsWrap.innerHTML = chips.map(function (c) { return c.html; }).join("") +
          '<button type="button" class="atm-cat__chip atm-cat__chip--clear" data-atm-clear-all>Limpiar todo</button>';
      } else {
        chipsWrap.hidden = true;
        chipsWrap.innerHTML = "";
      }
    }

    var componentHint = root.querySelector("[data-component-count]");
    var emptyEl = root.querySelector("[data-component-empty]");
    if (emptyEl) emptyEl.hidden = !hasActiveFilters(state) || visible > 0;
    if (componentHint && !hasActiveFilters(state)) {
      componentHint.textContent = "Busca en CPU, GPU, RAM, disco y pantalla, o combínalo con los filtros de abajo.";
    }
  }

  function initAll() {
    document.querySelectorAll("[data-atm-facets-root]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("atm:results-updated", function (event) {
    var root = event.detail && event.detail.root;
    if (!root) return;
    // Cards were replaced by a fresh server render (sort/pagination). `root`
    // itself (the section element) is untouched, so listeners bound in
    // bindRootEvents() are still attached — just reapply the existing state
    // to the new DOM (do NOT reset the bound flag, that would double-bind).
    initRoot(root);
  });

  window.addEventListener("popstate", function () {
    document.querySelectorAll("[data-atm-facets-root]").forEach(function (root) {
      root.__atmState = parseStateFromUrl();
      var searchInput = root.querySelector("[data-component-search]");
      if (searchInput) searchInput.value = root.__atmState.text || "";
      refresh(root);
    });
  });
})();
